import {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {SurveyStatus} from 'crqlar-react-common/dist/models/survey/survey.model';

import {buildAvailableVariables} from '../../../config/nodeVariables';
import {buildInviteOptions, inviteTarget, OTHER_TARGET} from '../../../config/surveyInvite';
import {isRemindableSurveyType} from '../../../config/surveyTriggers';
import AutomationTextField from '../variables/AutomationTextField';
import NodeEditorLayout, {NodeEditorState} from '../NodeEditorLayout';
import {SingleSelect} from '../../../../../common/newSelect';
import {NodeEditorV2Props} from '../types';

/**
 * SURVEY_INVITE: invites a guest and passes the link down. With both ids empty the API invites the run's own
 * guest, so the editor asks who only when there is a real choice (`inviteTarget`).
 */
const SurveyInviteEditorV2 = ({
    nodeConfig,
    setNodeConfig,
    upstreamNodes = [],
    availableSurveys = [],
    availableSurveysLoading,
    readOnly
}: NodeEditorV2Props) => {
    const {t} = useTranslation();

    const availableVariables = useMemo(() => buildAvailableVariables(upstreamNodes), [upstreamNodes]);

    // Anonymous surveys are shared by their own link and the API refuses to invite to one.
    const surveyOptions = useMemo(() => availableSurveys
        .filter(survey => isRemindableSurveyType(survey.type))
        .map(survey => ({
            value: survey.id,
            label: survey.status === SurveyStatus.PUBLISHED
                ? (survey.name || survey.id)
                : `${survey.name || survey.id} ${t('(draft)')}`.trim()
        })), [availableSurveys, t]);

    const inviteOptions = useMemo(() => buildInviteOptions(upstreamNodes, t), [upstreamNodes, t]);

    const surveyId: string = nodeConfig.surveyId || '';
    const reservationId: string = nodeConfig.reservationId || '';
    const profileId: string = nodeConfig.profileId || '';

    const selectedSurvey = availableSurveys.find(survey => survey.id === surveyId);
    // The catalogue wins over the stored snapshot, so a renamed survey reads by its current name.
    const surveyName = selectedSurvey?.name || nodeConfig.surveyName || '';
    const isDraft = !!selectedSurvey && selectedSurvey.status !== SurveyStatus.PUBLISHED;

    const target = inviteTarget(inviteOptions, nodeConfig);
    // "Another reservation or guest" empties both ids, which alone reads as the default, so the pick is held here.
    const [otherPicked, setOtherPicked] = useState(false);
    const selectedTarget = otherPicked || target.custom ? OTHER_TARGET : target.option?.value ?? OTHER_TARGET;

    const write = (patch: Record<string, any>) => setNodeConfig({...nodeConfig, ...patch});

    const handleSurvey = (value: string) => write({
        surveyId: value,
        // Stored beside the id so a card can name the survey without loading the catalogue.
        surveyName: availableSurveys.find(survey => survey.id === value)?.name
    });

    const handleTarget = (value: string) => {
        const option = inviteOptions.find(entry => entry.value === value);
        setOtherPicked(!option);
        write({
            reservationId: option?.reservationId ?? '',
            profileId: option?.profileId ?? ''
        });
    };

    const {state, summary}: {state: NodeEditorState; summary: string} = (() => {
        if (!surveyId) {
            return {
                state: 'wontRun',
                summary: surveyOptions.length
                    ? t('No survey picked yet — choose the survey this step invites guests to.')
                    : t('This organisation has no survey that can be sent by invitation yet. Create one first, anonymous surveys are shared by their own link instead.')
            };
        }

        if (isDraft) {
            return {
                state: 'wontRun',
                summary: t('‘{{survey}}’ is still a draft, and only a published survey can be sent. Publish it and this step starts working.', {survey: surveyName})
            };
        }

        if (target.nobody) {
            return {
                state: 'wontRun',
                summary: t('Nobody to invite yet: no step above this one brings a guest. Put an Audience step or a reservation trigger above it, or enter a guest id below.')
            };
        }

        const invited = target.option
            ? target.option.target
            : reservationId
                ? t('the guest on the reservation this step points at')
                : t('the guest this step points at');

        return {
            state: 'configured',
            summary: [
                t('Invites {{target}} to ‘{{survey}}’.', {target: invited, survey: surveyName}),
                t('The invitation link is available to the steps below as a value — put it in the email that follows.'),
                t('Running twice for the same reservation or guest reuses the same invitation, so the guest never loses a link they already have.')
            ].join(' ')
        };
    })();

    const idFields = (
        <>
            <AutomationTextField
                insertion={'step-value'}
                disabled={readOnly}
                label={t('Reservation id')}
                value={reservationId}
                onChange={value => write({reservationId: value})}
                helperText={target.field === 'ids'
                    ? t('The reservation to invite for. Type / to choose a value from an earlier step.')
                    : t('Leave empty to use the reservation that started the run. Type / to choose a value from an earlier step.')}
                variables={availableVariables}
            />
            <AutomationTextField
                insertion={'step-value'}
                disabled={readOnly}
                label={t('Guest id')}
                value={profileId}
                onChange={value => write({profileId: value})}
                helperText={t('For a guest without a reservation: the guest profile to invite. Type / to choose a value from an earlier step.')}
                variables={availableVariables}
            />
        </>
    );

    return (
        <NodeEditorLayout state={state} summary={summary}>
            {/* `loading` stays undefined once settled: only a non-empty list proves a stored survey is gone. */}
            <SingleSelect
                label={t('Survey')}
                value={surveyId}
                options={surveyOptions}
                onChange={handleSurvey}
                unknownLabel={nodeConfig.surveyName}
                loading={availableSurveysLoading || undefined}
                disabled={readOnly}
                disableClearable
            />

            {/* One obvious answer is named in the summary, not asked; see `inviteTarget`. */}
            {target.field === 'select' && (
                <SingleSelect
                    label={t('Who to invite')}
                    value={selectedTarget}
                    options={[
                        ...inviteOptions.map(({value, label}) => ({value, label})),
                        {value: OTHER_TARGET, label: t('Another reservation or guest')}
                    ]}
                    onChange={handleTarget}
                    disabled={readOnly}
                    disableClearable
                />
            )}

            {(target.field === 'ids' || (target.field === 'select' && selectedTarget === OTHER_TARGET)) && idFields}
        </NodeEditorLayout>
    );
};

export default SurveyInviteEditorV2;
